import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SortOrder } from './types';

export interface DefaultSort {
  field: string;
  order?: SortOrder;
}

export interface UseTableSortResult {
  /** Explicit sort field from the URL, or null when on the table default. */
  sortBy: string | null;
  sortOrder: SortOrder;
  /** The effective field being sorted (explicit, else the table default). */
  activeField: string | null;
  /** The effective direction (explicit, else the default's). */
  activeOrder: SortOrder;
  /** True when no explicit sort is set (showing the table's default order). */
  isDefault: boolean;
  setSort: (field: string, order: SortOrder) => void;
  /** Header-click behavior: flip direction if already active, else select. */
  toggleField: (field: string, defaultOrder?: SortOrder) => void;
  clear: () => void;
}

/**
 * URL-driven table sort (`sort_by` + `sort_order`). Every change resets
 * `page=1`. Pass the table's implicit default so the control/headers can show
 * the effective sort even before the user picks one. Shared by all tables.
 */
export function useTableSort(defaultSort?: DefaultSort): UseTableSortResult {
  const location = useLocation();
  const navigate = useNavigate();

  const { sortBy, sortOrder } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const sb = params.get('sort_by');
    const so: SortOrder = params.get('sort_order') === 'asc' ? 'asc' : 'desc';
    return { sortBy: sb, sortOrder: so };
  }, [location.search]);

  const commit = useCallback(
    (field: string | null, order: SortOrder) => {
      const params = new URLSearchParams(location.search);
      if (field) {
        params.set('sort_by', field);
        params.set('sort_order', order);
      } else {
        params.delete('sort_by');
        params.delete('sort_order');
      }
      params.set('page', '1');
      navigate(`?${params.toString()}`);
    },
    [location.search, navigate],
  );

  const activeField = sortBy ?? defaultSort?.field ?? null;
  const activeOrder: SortOrder = sortBy ? sortOrder : defaultSort?.order ?? 'desc';

  const setSort = useCallback((field: string, order: SortOrder) => commit(field, order), [commit]);

  const toggleField = useCallback(
    (field: string, defaultOrder: SortOrder = 'desc') => {
      if (activeField === field) commit(field, activeOrder === 'asc' ? 'desc' : 'asc');
      else commit(field, defaultOrder);
    },
    [activeField, activeOrder, commit],
  );

  const clear = useCallback(() => commit(null, 'desc'), [commit]);

  return {
    sortBy,
    sortOrder,
    activeField,
    activeOrder,
    isDefault: !sortBy,
    setSort,
    toggleField,
    clear,
  };
}
